/**
 * Tournois — vue liste + détail (réutilisabilité, extension Vague 3).
 * Réutilise le langage design (DomainCTA arène + StatusPill). Un match = un duel :
 * « Jouer mon match » passe par le même lancement que les duels (onPlayDuel).
 */
import { usePilotScope, getPilotScope } from "../lib/pilot-scope";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, ArrowLeft, Trophy, Coins, X, Play, Pencil, Trash2, UserPlus } from "lucide-react";
import { tournamentsApi, authApi, accountApi, type TournamentSummary, type DuelData } from "../lib/api";
import { DomainCTA } from "./ui/domain-cta";
import { StatusPill } from "./ui/status-pill";
import { EmptyState } from "./ui/empty-state";
import { Button } from "./ui/button";
import { canManageTournamentParticipant } from "../lib/tournament-rules";
import { canBeProposedAsParticipant } from "../lib/participant-eligibility";

// Qui gère quel tournoi : décidé par le serveur avec la règle partagée `canOrganizeTournament`
// (drapeaux `canManage` de la liste et `isOrganizer` du détail) — jamais recopiée ici.

export function TournamentsView({ profileType, onPlayDuel, canCreate = false, pilotScope = false }: {
  profileType: string;
  onPlayDuel?: (d: DuelData) => void;
  canCreate?: boolean;
  /** Dans Pilot : liste limitée au compte et au périmètre de la barre de Pilot (Q1 du 29/09/2026). */
  pilotScope?: boolean;
}) {
  const { t } = useTranslation();
  const tr = t;
  const [list, setList] = useState<TournamentSummary[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingT, setEditingT] = useState<TournamentSummary | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const pilot = usePilotScope();
  const scopeOpts = pilotScope ? { pilot: true } : undefined;
  // Un échec se DIT, avec « Réessayer » (audit du lot 5) : il s'affichait comme « Aucun tournoi ».
  const load = () => {
    setLoadError(false);
    return tournamentsApi.list(scopeOpts)
      .then(setList)
      .catch(() => { setList([]); setLoadError(true); })
      .finally(() => setLoading(false));
  };
  const removeTournament = async (t: TournamentSummary) => {
    if (!confirm(tr("tournaments.confirmDelete", { name: t.name }))) return;
    const r = await tournamentsApi.remove(t.id, scopeOpts);
    if (!r.ok && r.error) alert(r.error);
    load();
  };
  useEffect(() => { load(); }, [pilotScope ? pilot.account : null, pilotScope ? pilot.scope : null]);

  if (openId) return <TournamentDetail id={openId} profileType={profileType} pilotScope={pilotScope} onBack={() => { setOpenId(null); load(); }} onPlayDuel={onPlayDuel} />;
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "#C0423F" }} /></div>;
  if (loadError) return (
    <div role="alert" className="flex flex-wrap items-center gap-3 py-6 text-sm" style={{ color: "#C0423F" }}>
      {t("common.loadError")}
      <button type="button" className="font-semibold underline" onClick={() => { setLoading(true); load(); }}>{t("common.retry")}</button>
    </div>
  );

  const statusPill = (s: string) => s === "registration"
    ? <StatusPill tone="wait">{tr("tournaments.status.registration")}</StatusPill>
    : s === "running" ? <StatusPill gradientVar="var(--grad-tournament)">{tr("tournaments.status.running")}</StatusPill>
    : s === "finished" ? <StatusPill tone="success">{tr("tournaments.status.finished")}</StatusPill>
    : <StatusPill tone="info">{s}</StatusPill>;

  return (
    <div className="space-y-4">
      {/* Création réservée aux organisateurs (MNG/ORG/CSL/ADM) — exposée dans l'espace encadrant › Animer. */}
      {canCreate && (
        <div className="flex justify-end">
          <button
            data-tour="tournament-create"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-semibold text-white shadow-[var(--elev-1)]"
            style={{ background: "#926B00" }}
          >
            <Trophy className="h-4 w-4" />{tr("tournaments.organize")}
          </button>
        </div>
      )}

      {list.length === 0 ? (
        <EmptyState icon="🏆" iconGradient="var(--grad-tournament)"
          title={tr("tournaments.emptyTitle")}
          description={tr("tournaments.emptyBody")} />
      ) : (
        <div className="rounded-2xl border bg-white overflow-hidden">
          {list.map((t) => {
            const fmt = t.format === "league" ? tr("tournaments.format.league") : tr("tournaments.format.elimination");
            const diff = t.difficulty === "easy" ? tr("tournaments.difficulty.easy") : t.difficulty === "hard" ? tr("tournaments.difficulty.hard") : tr("tournaments.difficulty.medium");
            const met = t.metric === "progression" ? tr("tournaments.metric.progression") : tr("tournaments.metric.objective");
            const caseM = t.caseMode === "per_round" ? tr("tournaments.caseMode.perRound") : tr("tournaments.caseMode.same");
            const dt = (d?: string | null) => (d ? new Date(d).toLocaleDateString(tr("common.dateLocale"), { day: "numeric", month: "short" }) : "");
            let ctx = "";
            if (t.status === "registration") ctx = tr("tournaments.ctx.registration", { date: dt(t.createdAt) });
            else if (t.status === "running") ctx = tr("tournaments.ctx.running", { date: dt(t.startedAt), round: t.currentRound ?? 0, rounds: t.totalRounds ?? 0, done: t.matchesDone ?? 0, total: t.matchesTotal ?? 0 });
            else if (t.status === "finished") ctx = `${tr("tournaments.ctx.finished", { date: dt(t.finishedAt) })}${t.winnerName ? ` · 🏆 ${t.winnerName}` : ""}`;
            else ctx = tr("tournaments.cancelled");
            const ctxColor = t.status === "finished" ? "#1E7A3C" : t.status === "running" ? "#926B00" : "#6A7189";
            return (
              <div key={t.id} onClick={() => setOpenId(t.id)} className="w-full text-left px-4 py-3.5 border-b last:border-b-0 hover:bg-[#926B0008] cursor-pointer">
                <div className="flex items-center gap-2.5 mb-1.5">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl shrink-0" style={{ background: "#FBF3DA" }}><Trophy className="h-4 w-4" style={{ color: "#926B00" }} /></span>
                  <span className="font-semibold text-sm truncate flex-1" style={{ color: "#384872" }}>{t.name}</span>
                  {statusPill(t.status)}
                  {/* ORG : tous les tournois du compte ; MNG : les siens ; ADM / COO : compte choisi (30/09/2026). */}
                  {canCreate && t.canManage && (
                    <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => setEditingT(t)} title={tr("tournaments.edit")} className="p-1 text-gray-400 hover:text-[#926B00]"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => removeTournament(t)} title={tr("tournaments.delete")} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground pl-[42px]">
                  <span>{fmt}</span>
                  {t.simulatorName && <span>🎯 {t.simulatorName}</span>}
                  <span>{diff}</span>
                  <span>{met}</span>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground pl-[42px] mt-0.5">
                  <span>👥 {t.maxParticipants ? tr("tournaments.playersOfMax", { count: t.participantCount, max: t.maxParticipants }) : tr("tournaments.players", { count: t.participantCount })}</span>
                  {t.prizeCoins > 0 && <span>🪙 {t.prizeCoins}</span>}
                  {t.roundDeadlineHours != null && <span>⏱ {tr("tournaments.deadlinePerRound", { hours: t.roundDeadlineHours })}</span>}
                  <span>{caseM}</span>
                </div>
                <div className="text-xs font-medium pl-[42px] mt-1" style={{ color: ctxColor }}>{ctx}</div>
              </div>
            );
          })}
        </div>
      )}

      {(creating || editingT) && (
        <TournamentDialog
          profileType={profileType}
          pilotScope={pilotScope}
          editing={editingT}
          onClose={() => { setCreating(false); setEditingT(null); }}
          onSaved={() => { setCreating(false); setEditingT(null); load(); }}
        />
      )}
    </div>
  );
}

function TournamentDetail({ id, profileType, pilotScope = false, onBack, onPlayDuel }: { id: string; profileType: string; pilotScope?: boolean; onBack: () => void; onPlayDuel?: (d: DuelData) => void }) {
  const scopeOpts = pilotScope ? { pilot: true } : undefined;
  const { t: tr } = useTranslation();
  const [data, setData] = useState<any | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  // Un échec se DIT, avec « Réessayer », comme la liste : il laissait un chargement infini.
  // Un échec de rafraîchissement (toutes les 8 s) garde le tableau déjà affiché.
  const load = () => tournamentsApi.get(id, scopeOpts)
    .then((d) => { setData(d); setLoadError(false); })
    .catch(() => setLoadError(true));
  useEffect(() => { load(); const iv = setInterval(load, 8000); return () => clearInterval(iv); /* eslint-disable-next-line */ }, [id]);
  if (!data && loadError) return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"><ArrowLeft className="h-4 w-4" />{tr("common.back")}</button>
      <div role="alert" className="flex flex-wrap items-center gap-3 py-6 text-sm" style={{ color: "#C0423F" }}>
        {tr("common.loadError")}
        <button type="button" className="font-semibold underline" onClick={() => { setLoadError(false); load(); }}>{tr("common.retry")}</button>
      </div>
    </div>
  );
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "#C0423F" }} /></div>;

  const t = data.tournament;
  const join = async () => { setBusy(true); const r = await tournamentsApi.join(id, scopeOpts); if (!r.ok && r.error) alert(r.error); await load(); setBusy(false); };
  const start = async () => { setBusy(true); const r = await tournamentsApi.start(id, scopeOpts); if (!r.ok && r.error) alert(r.error); await load(); setBusy(false); };
  const play = () => data.myMatch && onPlayDuel?.({ id: data.myMatch.duelId, simulatorId: t.simulatorId, targetId: t.targetId, difficulty: t.difficulty, opponentName: data.myMatch.opponentName, stakeCoins: 0 } as DuelData);

  const rounds = [...new Set((data.matches as any[]).map((m) => m.round))].sort((a, b) => a - b);

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"><ArrowLeft className="h-4 w-4" />{tr("tournaments.back")}</button>

      <div className="relative overflow-hidden rounded-2xl p-4 md:p-5" style={{ background: "#FBF3DA", border: "1px solid #DED9E6", borderTopWidth: 3, borderTopColor: "#926B00", boxShadow: "var(--elev-1)" }}>
        <span className="pointer-events-none absolute -right-2 -bottom-5 text-7xl opacity-10">🏆</span>
        <div className="text-xs font-extrabold uppercase tracking-[0.12em]" style={{ color: "#926B00" }}>{t.format === "league" ? tr("tournaments.format.league") : tr("tournaments.format.elimination")}</div>
        <h2 className="text-xl font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{t.name}</h2>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm" style={{ color: "#5A6076" }}>
          {t.prizeCoins > 0 && <span>🪙 {tr("tournaments.prizeN", { n: t.prizeCoins })}</span>}
          <span>{tr("tournaments.players", { count: data.participants.length })}</span>
          {t.caseName && <span>{t.caseName}</span>}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {/* RH et GES ne s'inscrivent pas (même règle que les challenges, 30/09/2026) ; déjà inscrits, ils le restent. */}
        {t.status === "registration" && !data.joined && canBeProposedAsParticipant(profileType) && <Button variant="duel" onClick={join} disabled={busy}>{tr("tournaments.join")}</Button>}
        {t.status === "registration" && data.joined && <span className="text-sm" style={{ color: "#1E7A3C" }}>✓ {tr("tournaments.joined")}</span>}
        {t.status === "registration" && data.isOrganizer && <Button onClick={start} disabled={busy} style={{ background: "#926B00", color: "#fff" }}>{tr("tournaments.start")}</Button>}
        {data.myMatch && <Button variant="duel" onClick={play}><Play className="h-4 w-4 mr-1" />{tr("tournaments.playMatch")}</Button>}
        {t.status === "finished" && t.winnerId && <span className="text-sm font-semibold" style={{ color: "#926B00" }}>🏆 {tr("tournaments.championOf", { name: data.participants.find((p: any) => p.userId === t.winnerId)?.name ?? "—" })}</span>}
      </div>

      {/* Classement (league) */}
      {t.format === "league" && (
        <div className="rounded-2xl border bg-white overflow-hidden shadow-[var(--elev-2)]">
          <div className="px-4 py-2 text-xs font-bold uppercase tracking-wide" style={{ color: "#926B00" }}>{tr("tournaments.standings")}</div>
          {data.participants.map((p: any, i: number) => (
            <div key={p.userId} className="flex items-center gap-3 px-4 py-2 border-t">
              <span className="w-5 text-center font-bold" style={{ color: i === 0 ? "#926B00" : "#9AA0B0" }}>{i + 1}</span>
              <span className="flex-1 text-sm" style={{ color: "#384872" }}>{p.name}</span>
              <span className="text-xs text-muted-foreground">{tr("tournaments.played", { count: p.played })}</span>
              <span className="text-sm font-bold" style={{ color: "#1E7A3C" }}>{tr("tournaments.wins", { count: p.wins })}</span>
            </div>
          ))}
        </div>
      )}

      {/* Bracket (elimination) */}
      {t.format === "elimination" && (
        <div className="rounded-2xl border bg-white p-3 overflow-x-auto shadow-[var(--elev-2)]">
          <div className="flex gap-5" style={{ minWidth: rounds.length * 180 }}>
            {rounds.map((r) => (
              <div key={r} className="flex flex-col justify-around gap-2 min-w-[165px]">
                <div className="text-[0.7rem] font-bold uppercase tracking-wide text-center text-muted-foreground">{tr("tournaments.round", { n: r })}</div>
                {(data.matches as any[]).filter((m) => m.round === r).map((m) => (
                  <div key={m.id} className={`rounded-xl border overflow-hidden ${m.status === "running" ? "border-[#C0423F]" : ""}`} style={{ borderColor: m.status === "running" ? "#C0423F" : "var(--border)" }}>
                    <div className={`flex items-center gap-2 px-2.5 py-1.5 text-sm ${m.winnerId === m.playerAId ? "font-bold" : ""}`} style={{ color: m.winnerId === m.playerAId ? "#1E7A3C" : "#384872" }}>{m.winnerId === m.playerAId ? "✓ " : ""}{m.playerAName ?? "—"}</div>
                    <div className={`flex items-center gap-2 px-2.5 py-1.5 text-sm border-t ${m.winnerId === m.playerBId ? "font-bold" : ""}`} style={{ color: m.winnerId === m.playerBId ? "#1E7A3C" : "#384872" }}>{m.winnerId === m.playerBId ? "✓ " : ""}{m.playerBName ?? tr("tournaments.bye")}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TournamentDialog({ profileType, pilotScope = false, editing, onClose, onSaved }: { profileType: string; pilotScope?: boolean; editing: TournamentSummary | null; onClose: () => void; onSaved: () => void }) {
  const { t: tr } = useTranslation();
  const scopeOpts = pilotScope ? { pilot: true } : undefined;
  const isEdit = !!editing;
  const locked = isEdit && editing!.status !== "registration"; // config structurelle figée après démarrage
  const [agents, setAgents] = useState<Array<{ id: string; name: string }>>([]);
  const [allTargets, setAllTargets] = useState<any[]>([]);
  const [f, setF] = useState<any>(() => editing
    ? {
      name: editing.name, format: editing.format, simulatorId: (editing as any).simulatorId || "",
      targetId: (editing as any).targetId || "", caseMode: editing.caseMode || "same",
      difficulty: editing.difficulty || "medium", metric: editing.metric || "objective",
      maxParticipants: editing.maxParticipants != null ? String(editing.maxParticipants) : "",
      prizeCoins: String(editing.prizeCoins ?? 0), roundDeadlineHours: String(editing.roundDeadlineHours ?? 48),
    }
    : { name: "", format: "league", simulatorId: "", targetId: "", caseMode: "same", difficulty: "medium", metric: "objective", maxParticipants: "", prizeCoins: "0", roundDeadlineHours: "48" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Participants (édition uniquement) : liste des collaborateurs + set des inscrits.
  const [collabs, setCollabs] = useState<any[]>([]);
  const [partIds, setPartIds] = useState<string[]>([]);
  const [partNames, setPartNames] = useState<Record<string, string>>({});
  const [addSel, setAddSel] = useState("");

  useEffect(() => {
    authApi.getAgents().then((a: any[]) => setAgents((a || []).filter((x) => x.simulatorType !== "coach").map((x) => ({ id: x.id, name: x.name })))).catch(() => setAgents([]));
    // Dans Pilot : les cas du compte choisi (ADM et COO, décision du 30/09/2026).
    accountApi.getTargets(pilotScope ? getPilotScope().account ?? undefined : undefined).then((tg: any[]) => setAllTargets(tg || [])).catch(() => setAllTargets([]));
    if (editing) {
      // Dans Pilot : les collaborateurs du compte choisi (honoré par le serveur pour l'ADM et le COO).
      accountApi.getCollaborators(pilotScope ? getPilotScope().account ?? undefined : undefined).then((c: any[]) => setCollabs(c || [])).catch(() => setCollabs([]));
      tournamentsApi.get(editing.id, scopeOpts).then((d: any) => {
        setPartIds((d?.participants || []).map((p: any) => p.userId));
        setPartNames(Object.fromEntries((d?.participants || []).map((p: any) => [p.userId, p.name])));
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const cases = allTargets.filter((tg) => tg.simulatorId === f.simulatorId && (tg.personae || []).length > 0);
  const nameOf = (c: any) => `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() || c.email || "—";
  // Tous les inscrits (y compris hors de l'équipe d'un MNG) ; chacun ne se retire que si la règle
  // partagée le permet — pour un MNG, la liste des collaborateurs servie EST son sous-arbre.
  const mngSubtree = profileType === "MNG" ? collabs.map((c) => c.id) : null;
  const current = partIds.map((id) => collabs.find((c) => c.id === id) ?? { id, firstName: partNames[id] ?? "—", lastName: "" });
  const canRemove = (userId: string) => canManageTournamentParticipant({ profileType }, userId, mngSubtree);
  // RH et GES ne sont pas proposés (même règle que les challenges, 30/09/2026).
  const invitable = collabs.filter((c) => !partIds.includes(c.id) && canBeProposedAsParticipant(c.profileType));

  const addP = async (userId: string) => {
    if (!editing || !userId) return;
    setPartIds((p) => [...p, userId]); setAddSel("");
    const r = await tournamentsApi.manageParticipant(editing.id, userId, "add", scopeOpts);
    if (!r.ok) { setPartIds((p) => p.filter((x) => x !== userId)); if (r.error) alert(r.error); }
  };
  const removeP = async (userId: string) => {
    if (!editing) return;
    setPartIds((p) => p.filter((x) => x !== userId));
    const r = await tournamentsApi.manageParticipant(editing.id, userId, "remove", scopeOpts);
    if (!r.ok) { setPartIds((p) => [...p, userId]); if (r.error) alert(r.error); }
  };

  const submit = async () => {
    setError(null);
    if (!f.name.trim() || !f.simulatorId) { setError(tr("tournaments.dialog.nameSimRequired")); return; }
    setSaving(true);
    const payload = {
      ...f,
      maxParticipants: f.maxParticipants === "" ? null : Number(f.maxParticipants),
      prizeCoins: Number(f.prizeCoins) || 0, roundDeadlineHours: Number(f.roundDeadlineHours) || 48,
      targetId: f.caseMode === "same" ? (f.targetId || null) : null,
    };
    const res = isEdit ? await tournamentsApi.update(editing!.id, payload, scopeOpts) : await tournamentsApi.create(payload, scopeOpts);
    setSaving(false);
    if ("error" in res) { setError(res.error); return; }
    onSaved();
  };

  const inp = "w-full rounded-lg border px-3 py-2 text-sm"; const st = { borderColor: "#E6E4EE", color: "#384872" };
  const lbl = "block text-xs font-semibold mb-1"; const lst = { color: "#5A6076" };
  const dis = locked ? { opacity: 0.55, pointerEvents: "none" as const } : undefined;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl max-h-[90vh] overflow-y-auto" style={{ boxShadow: "var(--elev-3)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-lg" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>🏆 {isEdit ? tr("tournaments.dialog.editTitle") : tr("tournaments.dialog.newTitle")}</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
        </div>
        {locked && <p className="mb-3 rounded-lg px-3 py-2 text-xs" style={{ background: "#FBF3DA", color: "#926B00" }}>{tr("tournaments.dialog.locked")}</p>}
        <div className="space-y-3">
          <div><label className={lbl} style={lst}>{tr("tournaments.dialog.name")}</label><input className={inp} style={st} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div style={dis}><label className={lbl} style={lst}>{tr("tournaments.dialog.format")}</label>
            <select className={inp} style={st} value={f.format} onChange={(e) => setF({ ...f, format: e.target.value })}>
              <option value="league">{tr("tournaments.dialog.formatLeague")}</option>
              <option value="elimination">{tr("tournaments.format.elimination")}</option>
            </select>
          </div>
          <div style={dis}><label className={lbl} style={lst}>{tr("tournaments.dialog.simulator")}</label>
            <select className={inp} style={st} value={f.simulatorId} onChange={(e) => setF({ ...f, simulatorId: e.target.value, targetId: "" })}>
              <option value="">{tr("tournaments.dialog.choose")}</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          {f.simulatorId && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" style={dis}>
              <div><label className={lbl} style={lst}>{tr("tournaments.dialog.case")}</label>
                <select className={inp} style={st} value={f.caseMode === "same" ? f.targetId : "__per__"} onChange={(e) => e.target.value === "__per__" ? setF({ ...f, caseMode: "per_round", targetId: "" }) : setF({ ...f, caseMode: "same", targetId: e.target.value })}>
                  <option value="">{tr("tournaments.dialog.fixedCase")}</option>
                  {cases.map((tg) => <option key={tg.id} value={tg.id}>{tg.displayName || tg.industry || tr("tournaments.dialog.case")}</option>)}
                  <option value="__per__">{tr("tournaments.dialog.perRound")}</option>
                </select>
              </div>
              <div><label className={lbl} style={lst}>{tr("tournaments.dialog.difficulty")}</label>
                <select className={inp} style={st} value={f.difficulty} onChange={(e) => setF({ ...f, difficulty: e.target.value })}>
                  <option value="easy">{tr("tournaments.difficulty.easy")}</option><option value="medium">{tr("tournaments.difficulty.medium")}</option><option value="hard">{tr("tournaments.difficulty.hard")}</option>
                </select>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div style={dis}><label className={lbl} style={lst}>{tr("tournaments.dialog.maxPlayers")}</label><input type="number" className={inp} style={st} placeholder={tr("tournaments.dialog.unlimited")} value={f.maxParticipants} onChange={(e) => setF({ ...f, maxParticipants: e.target.value })} /></div>
            <div><label className={lbl} style={lst}>{tr("tournaments.dialog.prizeCoins")}</label><input type="number" className={inp} style={st} value={f.prizeCoins} onChange={(e) => setF({ ...f, prizeCoins: e.target.value })} /></div>
          </div>
          <div style={dis}><label className={lbl} style={lst}>{tr("tournaments.dialog.winnerBy")}</label>
            <select className={inp} style={st} value={f.metric} onChange={(e) => setF({ ...f, metric: e.target.value })}>
              <option value="objective">{tr("tournaments.dialog.metricObjective")}</option><option value="progression">{tr("tournaments.dialog.metricProgression")}</option>
            </select>
          </div>

          {/* Participants — édition, avant démarrage */}
          {isEdit && (
            <div className="rounded-xl border p-3" style={{ borderColor: "#E6E4EE" }}>
              <div className="flex items-center gap-2 mb-2">
                <UserPlus className="h-4 w-4" style={{ color: "#926B00" }} />
                <span className="text-xs font-semibold" style={lst}>{tr("tournaments.dialog.participants")} ({current.length})</span>
              </div>
              {current.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {current.map((c) => (
                    <span key={c.id} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs" style={{ background: "#FBF3DA", color: "#384872" }}>
                      {nameOf(c)}
                      {!locked && canRemove(c.id) && <button onClick={() => removeP(c.id)} className="text-[#926B00] hover:text-red-600"><X className="h-3 w-3" /></button>}
                    </span>
                  ))}
                </div>
              )}
              {locked ? (
                <p className="text-xs text-muted-foreground">{tr("tournaments.dialog.listLocked")}</p>
              ) : invitable.length === 0 ? (
                <p className="text-xs text-muted-foreground">{tr("tournaments.dialog.allIn")}</p>
              ) : (
                <select className={inp} style={st} value={addSel} onChange={(e) => addP(e.target.value)}>
                  <option value="">{tr("tournaments.dialog.invite")}</option>
                  {invitable.map((c) => <option key={c.id} value={c.id}>{nameOf(c)}</option>)}
                </select>
              )}
            </div>
          )}

          {error && <p className="text-sm font-medium" style={{ color: "#C0423F" }}>{error}</p>}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{tr("tournaments.dialog.close")}</Button>
          <Button onClick={submit} disabled={saving} style={{ backgroundImage: "var(--grad-tournament)", color: "#fff" }}>{saving ? "…" : isEdit ? tr("tournaments.dialog.save") : tr("tournaments.dialog.create")}</Button>
        </div>
      </div>
    </div>
  );
}
